import { noop } from "lodash-es";
import {
  getRangeLength,
  trimRangeByAnotherRange,
  trimNumberToFitWithin0ToAnotherNumber,
  normalizePositionByRangeLength,
  expandOrContractRangeToPosition
} from "@teselagen/range-utils";
let dragInProgress = false;
let dragOccurred = false;
let lastDragEndTime = 0;
let selectionStartOrEndGrabbed;
let caretPositionOnDragStart;
export const editorDragged = function({
  nearestCaretPos,
  doNotWrapOrigin,
  caretPosition = -1,
  easyStore,
  caretPositionUpdate = noop,
  selectionLayerUpdate = noop,
  selectionLayer = { start: -1, end: -1 },
  sequenceLength
}) {
  if (easyStore && easyStore.selectionLayer) {
    caretPosition = easyStore.caretPosition;
    selectionLayer = easyStore.selectionLayer;
  }
  if (!dragInProgress) {
    dragInProgress = (/* @__PURE__ */ new Date()).getTime();
  }
  dragOccurred = true;
  if (selectionLayer.start > -1 && selectionStartOrEndGrabbed === "start") {
    handleSelectionStartGrabbed({
      caretPosition,
      selectionLayer,
      caretPositionUpdate,
      selectionLayerUpdate,
      nearestCaretPos,
      sequenceLength,
      doNotWrapOrigin
    });
  } else if (selectionLayer.start > -1 && selectionStartOrEndGrabbed === "end") {
    handleSelectionEndGrabbed({
      caretPosition,
      selectionLayer,
      caretPositionUpdate,
      selectionLayerUpdate,
      nearestCaretPos,
      sequenceLength,
      doNotWrapOrigin
    });
  } else {
    handleNoSelectionLayerYet({
      caretPosition: caretPositionOnDragStart,
      selectionLayer: caretPositionOnDragStart ? { start: -1, end: -1 } : selectionLayer,
      selectionLayerUpdate,
      nearestCaretPos,
      sequenceLength,
      doNotWrapOrigin
    });
  }
};
export const editorClicked = function({
  nearestCaretPos,
  shiftHeld,
  updateSelectionOrCaret: updateSelectionOrCaret2
}) {
  const now = (/* @__PURE__ */ new Date()).getTime();
  if (dragOccurred || lastDragEndTime && now - lastDragEndTime < 250) {
    return;
  }
  updateSelectionOrCaret2(shiftHeld, nearestCaretPos);
};
export const editorDragStarted = function(opts = {}) {
  document?.body.classList.add("sequenceDragging");
  window.__veDragging = true;
  dragInProgress = (/* @__PURE__ */ new Date()).getTime();
  dragOccurred = false;
  caretPositionOnDragStart = opts.nearestCaretPos;
  selectionStartOrEndGrabbed = opts.selectionStartGrabbed ? "start" : opts.selectionEndGrabbed ? "end" : null;
};
export const editorDragStopped = function() {
  document.body.classList.remove("sequenceDragging");
  window.__veDragging = false;
  caretPositionOnDragStart = null;
  if (dragOccurred) {
    lastDragEndTime = (/* @__PURE__ */ new Date()).getTime();
  }
  setTimeout(function() {
    dragInProgress = false;
    dragOccurred = false;
  }, 200);
};
export function handleCaretMoved({
  moveBy,
  circular,
  sequenceLength,
  caretPosition,
  selectionLayer,
  shiftHeld,
  type,
  caretPositionUpdate,
  selectionLayerUpdate
}) {
  let newCaretPosition;
  if (selectionLayer.start > -1) {
    if (shiftHeld) {
      newCaretPosition = normalizeNewCaretPos(
        Number(
          (selectionLayer.cursorAtEnd ? selectionLayer.end + 1 : selectionLayer.start) + moveBy
        ),
        sequenceLength,
        circular
      );
      let anchorPos;
      if (selectionLayer.start <= selectionLayer.end) {
        if (selectionLayer.cursorAtEnd) {
          if (newCaretPosition === selectionLayer.start && moveBy < 0) {
            return caretPositionUpdate(newCaretPosition);
          }
          anchorPos = selectionLayer.start;
        } else {
          if (newCaretPosition === selectionLayer.end + 1 && moveBy > 0) {
            return caretPositionUpdate(newCaretPosition);
          }
          anchorPos = selectionLayer.end + 1;
        }
        if (newCaretPosition > anchorPos) {
          if (circular && selectionLayer.start + moveBy < 0) {
            selectionLayerUpdate({
              start: newCaretPosition,
              end: anchorPos - 1,
              cursorAtEnd: false
            });
          } else {
            selectionLayerUpdate({
              start: anchorPos,
              end: normalizePositionByRangeLength(
                newCaretPosition - 1,
                sequenceLength
              ),
              cursorAtEnd: true
            });
          }
        } else {
          if (circular && selectionLayer.end + moveBy >= sequenceLength) {
            selectionLayerUpdate({
              start: anchorPos,
              end: normalizePositionByRangeLength(
                newCaretPosition - 1,
                sequenceLength
              ),
              cursorAtEnd: true
            });
          } else {
            selectionLayerUpdate({
              start: newCaretPosition,
              end: normalizePositionByRangeLength(
                anchorPos - 1,
                sequenceLength
              ),
              cursorAtEnd: false
            });
          }
        }
      } else {
        if (selectionLayer.cursorAtEnd) {
          anchorPos = selectionLayer.start;
        } else {
          anchorPos = selectionLayer.end + 1;
        }
        if (newCaretPosition <= anchorPos && !(!selectionLayer.cursorAtEnd && newCaretPosition - moveBy < 0) || // if the move by is crossing the origin then we should make the new selection non circular
        selectionLayer.cursorAtEnd && selectionLayer.end + moveBy < 0) {
          selectionLayerUpdate({
            start: anchorPos,
            end: normalizePositionByRangeLength(
              newCaretPosition - 1,
              sequenceLength
            ),
            cursorAtEnd: true
          });
        } else {
          selectionLayerUpdate({
            start: normalizePositionByRangeLength(
              newCaretPosition,
              sequenceLength
            ),
            end: normalizePositionByRangeLength(anchorPos - 1, sequenceLength),
            cursorAtEnd: false
          });
        }
      }
    } else {
      if (moveBy === 0) {
        if (type === "moveCaretRightOne") {
          return caretPositionUpdate(selectionLayer.end + 1);
        } else if (type === "moveCaretLeftOne") {
          return caretPositionUpdate(selectionLayer.start);
        } else {
          throw new Error("this case should not be hit...");
        }
      } else if (moveBy > 0) {
        newCaretPosition = normalizeNewCaretPos(
          Number(selectionLayer.end + moveBy),
          sequenceLength,
          circular
        );
        caretPositionUpdate(1);
      } else {
        newCaretPosition = normalizeNewCaretPos(
          Number(selectionLayer.start + moveBy),
          sequenceLength,
          circular
        );
        caretPositionUpdate(newCaretPosition);
      }
    }
  } else {
    newCaretPosition = normalizeNewCaretPos(
      Number(caretPosition + moveBy),
      sequenceLength,
      circular
    );
    if (shiftHeld) {
      if (moveBy > 0) {
        if (newCaretPosition === caretPosition) {
          caretPositionUpdate(newCaretPosition);
        } else {
          selectionLayerUpdate({
            start: caretPosition,
            end: normalizePositionByRangeLength(
              newCaretPosition - 1,
              sequenceLength
            ),
            cursorAtEnd: true
          });
        }
      } else {
        if (newCaretPosition === caretPosition) {
          caretPositionUpdate(newCaretPosition);
        } else {
          selectionLayerUpdate({
            start: newCaretPosition,
            end: normalizePositionByRangeLength(
              caretPosition - 1,
              sequenceLength
            ),
            cursorAtEnd: false
          });
        }
      }
    } else {
      caretPositionUpdate(newCaretPosition);
    }
  }
}
export function normalizeNewCaretPos(caretPosition, sequenceLength, circular) {
  if (circular) {
    return normalizePositionByRangeLength(caretPosition, sequenceLength, true);
  } else {
    return trimNumberToFitWithin0ToAnotherNumber(caretPosition, sequenceLength);
  }
}
export function handleSelectionStartGrabbed({
  caretPosition,
  selectionLayer,
  selectionLayerUpdate,
  nearestCaretPos,
  sequenceLength,
  doNotWrapOrigin,
  caretPositionUpdate
}) {
  if (selectionLayer.start < 0) {
    handleNoSelectionLayerYet({
      caretPosition,
      selectionLayer,
      selectionLayerUpdate,
      nearestCaretPos,
      sequenceLength,
      doNotWrapOrigin
    });
  } else {
    if (doNotWrapOrigin && selectionLayer.end > -1 && nearestCaretPos === selectionLayer.end + 1) {
      caretPositionUpdate(nearestCaretPos);
      caretPositionOnDragStart = nearestCaretPos;
    } else if (doNotWrapOrigin && nearestCaretPos > selectionLayer.end + 1) {
      caretPositionOnDragStart = selectionLayer.end + 1;
      selectionLayerUpdate({
        start: selectionLayer.end + 1,
        end: nearestCaretPos,
        cursorAtEnd: true
      });
      selectionStartOrEndGrabbed = "end";
    } else {
      selectionStartOrEndGrabbed = "start";
      selectionLayerUpdate({
        start: nearestCaretPos,
        end: selectionLayer.end,
        cursorAtEnd: false
      });
    }
  }
}
export function handleSelectionEndGrabbed({
  caretPosition,
  selectionLayer,
  selectionLayerUpdate,
  nearestCaretPos,
  sequenceLength,
  doNotWrapOrigin,
  caretPositionUpdate
}) {
  if (selectionLayer.start < 0) {
    handleNoSelectionLayerYet({
      caretPosition,
      selectionLayerUpdate,
      nearestCaretPos,
      sequenceLength,
      doNotWrapOrigin
    });
  } else {
    if (doNotWrapOrigin && selectionLayer.start > -1 && nearestCaretPos === selectionLayer.start) {
      caretPositionUpdate(nearestCaretPos);
      caretPositionOnDragStart = nearestCaretPos;
    } else if (doNotWrapOrigin && nearestCaretPos < selectionLayer.start) {
      selectionLayerUpdate({
        start: nearestCaretPos,
        end: selectionLayer.start - 1,
        cursorAtEnd: false
      });
      caretPositionOnDragStart = selectionLayer.start;
      selectionStartOrEndGrabbed = "start";
    } else {
      selectionStartOrEndGrabbed = "end";
      const newEnd = Math.min(nearestCaretPos - 1, sequenceLength - 1);
      selectionLayerUpdate({
        start: selectionLayer.start,
        end: newEnd,
        cursorAtEnd: true
      });
    }
  }
}
export function handleNoSelectionLayerYet({
  caretPosition,
  selectionLayerUpdate,
  nearestCaretPos,
  sequenceLength,
  doNotWrapOrigin
}) {
  const dragEnd = {
    start: caretPosition,
    end: normalizePositionByRangeLength(
      nearestCaretPos - 1,
      sequenceLength,
      true
    )
  };
  const dragStart = {
    start: nearestCaretPos,
    end: normalizePositionByRangeLength(caretPosition - 1, sequenceLength, true)
  };
  if (caretPosition === nearestCaretPos) {
    return;
  } else if (doNotWrapOrigin && caretPosition < nearestCaretPos || !doNotWrapOrigin && getRangeLength(dragEnd, sequenceLength) < getRangeLength(dragStart, sequenceLength)) {
    selectionStartOrEndGrabbed = "end";
    selectionLayerUpdate({ ...dragEnd, cursorAtEnd: true });
    caretPositionOnDragStart = null;
  } else {
    selectionStartOrEndGrabbed = "start";
    selectionLayerUpdate({ ...dragStart, cursorAtEnd: false });
    caretPositionOnDragStart = null;
  }
}
export function updateSelectionOrCaret({
  shiftHeld,
  sequenceLength,
  newRangeOrCaret,
  caretPosition,
  selectionLayer,
  selectionLayerUpdate = noop,
  caretPositionUpdate = noop,
  doNotWrapOrigin
}) {
  let newCaret;
  let newRange;
  if (typeof newRangeOrCaret !== "object") {
    newCaret = newRangeOrCaret;
  } else {
    newRange = {
      isFromRowView: newRangeOrCaret.isFromRowView,
      start: newRangeOrCaret.start,
      end: newRangeOrCaret.end,
      forward: newRangeOrCaret.forward,
      forceUpdate: newRangeOrCaret.forceUpdate,
      overlapsSelf: newRangeOrCaret.overlapsSelf,
      isWrappedAddon: newRangeOrCaret.isWrappedAddon
    };
  }
  if (shiftHeld) {
    if (selectionLayer.start > -1) {
      if (newCaret > -1) {
        const distanceFromStart = getMinRangeLength(
          selectionLayer.start,
          newCaret,
          sequenceLength,
          doNotWrapOrigin
        );
        const distanceFromEnd = getMinRangeLength(
          selectionLayer.end,
          newCaret,
          sequenceLength,
          doNotWrapOrigin
        );
        if (distanceFromStart < distanceFromEnd) {
          selectionLayerUpdate({
            start: newCaret,
            end: selectionLayer.end,
            cursorAtEnd: false
          });
        } else {
          selectionLayerUpdate({
            start: selectionLayer.start,
            end: normalizePositionByRangeLength(
              newCaret - 1,
              sequenceLength,
              true
            ),
            cursorAtEnd: true
          });
        }
      } else {
        const selectionFullyContained = !trimRangeByAnotherRange(
          selectionLayer,
          newRange
        );
        if (selectionFullyContained) {
          return selectionLayerUpdate(newRange);
        }
        const newRangeFullyContained = !trimRangeByAnotherRange(
          newRange,
          selectionLayer
        );
        const { newRange: range1 } = expandOrContractRangeToPosition(
          selectionLayer,
          newRange.start,
          sequenceLength
        );
        const { newRange: range2 } = expandOrContractRangeToPosition(
          selectionLayer,
          newRange.end + 1,
          sequenceLength
        );
        const range1Shorter = getRangeLength(range1, sequenceLength) < getRangeLength(range2, sequenceLength);
        if (newRangeFullyContained) {
          range1Shorter ? selectionLayerUpdate({ ...range1, cursorAtEnd: false }) : selectionLayerUpdate({ ...range2, cursorAtEnd: true });
        } else {
          const candidate1 = {
            isFromRowView: newRange.isFromRowView,
            start: selectionLayer.start,
            end: newRange.end,
            forward: newRange.forward,
            forceUpdate: newRange.forceUpdate,
            overlapsSelf: newRange.overlapsSelf,
            isWrappedAddon: newRange.isWrappedAddon
          };
          const candidate2 = {
            isFromRowView: newRange.isFromRowView,
            start: newRange.start,
            end: selectionLayer.end,
            forward: newRange.forward,
            forceUpdate: newRange.forceUpdate,
            overlapsSelf: newRange.overlapsSelf,
            isWrappedAddon: newRange.isWrappedAddon
          };
          if (doNotWrapOrigin) {
            const wraps1 = candidate1.start > candidate1.end;
            const wraps2 = candidate2.start > candidate2.end;
            if (wraps1 && !wraps2) {
              selectionLayerUpdate({ ...candidate2, cursorAtEnd: false });
            } else if (wraps2 && !wraps1) {
              selectionLayerUpdate({ ...candidate1, cursorAtEnd: true });
            } else if (!wraps1 && !wraps2) {
              const len1 = getRangeLength(candidate1, sequenceLength);
              const len2 = getRangeLength(candidate2, sequenceLength);
              if (len1 >= len2) {
                selectionLayerUpdate({ ...candidate1, cursorAtEnd: true });
              } else {
                selectionLayerUpdate({ ...candidate2, cursorAtEnd: false });
              }
            } else {
              selectionLayerUpdate({ ...newRange, cursorAtEnd: true });
            }
          } else {
            const len1 = getRangeLength(candidate1, sequenceLength);
            const len2 = getRangeLength(candidate2, sequenceLength);
            if (len1 <= len2) {
              selectionLayerUpdate({ ...candidate1, cursorAtEnd: true });
            } else {
              selectionLayerUpdate({ ...candidate2, cursorAtEnd: false });
            }
          }
        }
      }
    } else if (caretPosition > -1) {
      if (newCaret > -1) {
        handleNoSelectionLayerYet({
          caretPosition,
          selectionLayer,
          selectionLayerUpdate,
          nearestCaretPos: newCaret,
          sequenceLength,
          doNotWrapOrigin
        });
      } else {
        simpleUpdate();
      }
    } else {
      simpleUpdate();
    }
  } else {
    simpleUpdate();
  }
  function simpleUpdate() {
    if (newCaret > -1) {
      caretPositionUpdate(newCaret);
    } else {
      selectionLayerUpdate(newRange);
    }
  }
}
function getMinRangeLength(start, end, sequenceLength, doNotWrapOrigin) {
  const range1 = getRangeLength({ start, end }, sequenceLength);
  const range2 = getRangeLength({ start: end, end: start }, sequenceLength);
  if (doNotWrapOrigin) {
    if (start < end) {
      return range1;
    } else {
      return range2;
    }
  }
  return range1 < range2 ? range1 : range2;
}
